import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Briefcase, CheckCircle, Clock, DollarSign, ArrowRight,
  TrendingUp, Wallet, RefreshCw, Loader2, BarChart3,
  X, ChevronRight, MapPin, Calendar, User, AlertCircle,
  Bell, MoreHorizontal, Wrench,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTimezone } from "../../contexts/TimezoneContext";
import { formatScheduledTime, extractTimeFromDate } from "../../utils/dateUtils";

// ─── Formatters ─────────────────────────────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return dateStr; }
};

const formatTime = (dateStr) => {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  } catch { return dateStr; }
};

const formatShortDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    // 17.03.23-style — readable, matches the spec screenshot's "Updated 17.03.23"
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = String(d.getFullYear()).slice(-2);
    return `${dd}.${mm}.${yy}`;
  } catch { return ""; }
};

const moneyFmt = (n) =>
  `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const DATE_FILTERS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This Week" },
  { value: "last_week", label: "Last Week" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "this_year", label: "This Year" },
  { value: "all", label: "All Time" },
];

const STATUS_STYLE = {
  pending: { bg: "bg-amber-50", text: "text-amber-600", border: "border-amber-200" },
  estimate: { bg: "bg-orange-50", text: "text-orange-600", border: "border-orange-200" },
  scheduled: { bg: "bg-blue-50", text: "text-blue-600", border: "border-blue-200" },
  in_progress: { bg: "bg-purple-50", text: "text-purple-600", border: "border-purple-200" },
  completed: { bg: "bg-emerald-50", text: "text-emerald-600", border: "border-emerald-200" },
  cancelled: { bg: "bg-red-50", text: "text-red-600", border: "border-red-200" },
};

const getAuthConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("tech_token") || localStorage.getItem("token")}` },
});

// ─── Reusable building blocks ───────────────────────────────────────────

// Compact (key) stat card — still used by the secondary "Active / Pending"
// row so existing testids stay stable for tests/automation.
function StatCard({ icon: Icon, label, value, sub, color, bgColor, onClick, clickable }) {
  return (
    <Card
      className={`bg-card border border-border/60 shadow-sm transition-all duration-200 ${clickable ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-md active:scale-[0.98]" : ""}`}
      onClick={clickable ? onClick : undefined}
      data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[11px] text-muted-foreground/70 uppercase tracking-wider mb-1.5 font-medium">{label}</p>
            <p className={`text-2xl font-bold font-['Manrope'] ${color}`}>{value}</p>
            {sub && <p className="text-[11px] text-muted-foreground/50 mt-0.5">{sub}</p>}
          </div>
          <div className={`w-9 h-9 rounded-lg ${bgColor} flex items-center justify-center shrink-0`}>
            <Icon className={`w-4 h-4 ${color}`} />
          </div>
        </div>
        {clickable && (
          <div className="flex items-center gap-1 mt-2.5 text-[10px] text-muted-foreground/40">
            <span>Tap to view details</span>
            <ChevronRight className="w-3 h-3" />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
  return (
    <Badge className={`text-[10px] capitalize px-2 py-0.5 border ${s.bg} ${s.text} ${s.border} font-medium`}>
      {(status || "").replace(/_/g, " ")}
    </Badge>
  );
};

// Initials avatar for the tech name pill on the Upcoming Work card.
const InitialsAvatar = ({ name, className = "" }) => {
  const initials = (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase() || "?";
  return (
    <div className={`w-6 h-6 rounded-full bg-primary/15 text-primary flex items-center justify-center text-[10px] font-bold ${className}`}>
      {initials}
    </div>
  );
};

// ─── Job List Slide-Out Panel ─────────────────────────────────────────
const JobListPanel = ({ title, jobs, loading, onClose, onJobClick }) => (
  <div className="fixed inset-0 bg-black/30 backdrop-blur-[2px] z-50 flex justify-end" onClick={onClose} data-testid="job-list-panel">
    <div
      className="w-full max-w-md h-full bg-background overflow-auto border-l border-border shadow-2xl animate-in slide-in-from-right"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="sticky top-0 z-10 p-4 bg-background border-b border-border flex items-center justify-between">
        <h2 className="text-base font-bold text-foreground font-['Manrope']">{title}</h2>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1 rounded-md hover:bg-muted" data-testid="close-panel-btn">
          <X className="w-5 h-5" />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : jobs.length === 0 ? (
        <div className="text-center py-16 px-4">
          <Briefcase className="w-10 h-10 mx-auto mb-3 text-muted-foreground/20" />
          <p className="text-sm text-muted-foreground">No jobs found</p>
        </div>
      ) : (
        <div className="divide-y divide-border/50">
          {jobs.map((job, i) => (
            <div
              key={job.job_id || i}
              className="p-4 hover:bg-muted/40 transition-colors cursor-pointer group"
              onClick={() => onJobClick(job)}
              data-testid={`panel-job-${job.job_id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h4 className="text-sm font-semibold text-foreground truncate">{job.title || job.service_type || "Job"}</h4>
                    {job.job_display_id && (
                      <span className="text-[10px] font-mono text-muted-foreground/50 bg-muted px-1.5 py-0.5 rounded">{job.job_display_id}</span>
                    )}
                  </div>
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <User className="w-3 h-3" />
                      <span className="truncate">{job.customer_name || "—"}</span>
                    </div>
                    {job.scheduled_date && (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground/60">
                        <Calendar className="w-3 h-3" />
                        <span>{formatDate(job.scheduled_date)}</span>
                        {(job.scheduled_time || job.scheduled_date) && <span>at {formatScheduledTime(job.scheduled_time) || extractTimeFromDate(job.scheduled_date)}</span>}
                      </div>
                    )}
                    {job.customer_address && (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground/50">
                        <MapPin className="w-3 h-3" />
                        <span className="truncate">{job.customer_address}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                  <StatusBadge status={job.status} />
                  {(job.total_charged > 0 || job.total_price > 0) && (
                    <span className="text-xs font-bold text-emerald-600">${(job.total_charged || job.total_price || 0).toLocaleString()}</span>
                  )}
                  {job.technician_commission_amount > 0 && (
                    <span className="text-[10px] text-purple-500 font-medium">+${job.technician_commission_amount.toFixed(2)}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

// ─── Job Detail Slide-Out ──────────────────────────────────────────────
const JobDetailPanel = ({ job, onClose }) => {
  if (!job) return null;
  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-[2px] z-[60] flex justify-end" onClick={onClose} data-testid="job-detail-panel">
      <div
        className="w-full max-w-md h-full bg-background overflow-auto border-l border-border shadow-2xl animate-in slide-in-from-right"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 p-4 bg-background border-b border-border">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <h2 className="text-base font-bold text-foreground font-['Manrope'] truncate">{job.title || job.service_type || "Job"}</h2>
              <div className="flex items-center gap-2 mt-1">
                {job.job_display_id && <span className="text-[10px] font-mono text-muted-foreground/50">{job.job_display_id}</span>}
                <StatusBadge status={job.status} />
              </div>
            </div>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1 rounded-md hover:bg-muted" data-testid="close-detail-panel">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-4 space-y-3">
          <DetailSection title="Customer" icon={<User className="w-3.5 h-3.5 text-primary" />}>
            <p className="text-sm font-medium text-foreground">{job.customer_name || "—"}</p>
            {job.customer_address && (
              <div className="flex items-start gap-2 text-muted-foreground text-xs mt-1">
                <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                <span>{job.customer_address}</span>
              </div>
            )}
            {job.customer_email && <p className="text-muted-foreground/60 text-xs mt-1">{job.customer_email}</p>}
          </DetailSection>

          {(job.scheduled_date || job.scheduled_time) && (
            <DetailSection title="Schedule" icon={<Calendar className="w-3.5 h-3.5 text-blue-500" />}>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {job.scheduled_date && <div><p className="text-muted-foreground/60 text-[10px] mb-0.5">Date</p><p className="text-foreground font-medium">{formatDate(job.scheduled_date)}</p></div>}
                {(job.scheduled_time || job.scheduled_date) && <div><p className="text-muted-foreground/60 text-[10px] mb-0.5">Time</p><p className="text-foreground font-medium">{formatScheduledTime(job.scheduled_time) || extractTimeFromDate(job.scheduled_date) || formatTime(job.scheduled_date)}</p></div>}
              </div>
            </DetailSection>
          )}

          {job.service_name && (
            <DetailSection title="Service" icon={<Briefcase className="w-3.5 h-3.5 text-purple-500" />}>
              <p className="text-foreground text-sm">{job.service_name}</p>
            </DetailSection>
          )}

          {job.notes && (
            <DetailSection title="Notes" icon={<AlertCircle className="w-3.5 h-3.5 text-amber-500" />}>
              <p className="text-muted-foreground text-sm whitespace-pre-wrap">{job.notes}</p>
            </DetailSection>
          )}

          {job.parts?.length > 0 && (
            <DetailSection title="Parts" icon={<Briefcase className="w-3.5 h-3.5 text-orange-500" />}>
              <div className="space-y-1.5">
                {job.parts.map((p, idx) => (
                  <div key={idx} className="flex justify-between text-sm p-2.5 rounded-lg bg-muted/50">
                    <span className="text-foreground">{p.name} {p.quantity > 1 && <span className="text-muted-foreground">x{p.quantity}</span>}</span>
                    <span className="text-foreground font-medium">${((p.cost || 0) * (p.quantity || 1)).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </DetailSection>
          )}

          {(job.total_price > 0 || job.total_charged > 0) && (
            <div className="rounded-xl p-4 bg-emerald-50/50 border border-emerald-200/50">
              <h4 className="text-muted-foreground text-[10px] font-medium uppercase tracking-wider mb-3">Financial Summary</h4>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Total Price</span><span className="text-foreground font-medium">${(job.total_price || 0).toFixed(2)}</span></div>
                {job.total_charged > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Charged</span><span className="text-foreground font-medium">${job.total_charged.toFixed(2)}</span></div>}
                {job.technician_commission_amount > 0 && (
                  <div className="flex justify-between pt-2 border-t border-emerald-200/30">
                    <span className="text-muted-foreground font-medium">Your Commission</span>
                    <span className="text-emerald-600 font-bold">${job.technician_commission_amount.toFixed(2)}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DetailSection = ({ title, icon, children }) => (
  <div className="rounded-xl p-4 bg-card border border-border/50">
    <h4 className="text-muted-foreground/70 text-[10px] font-medium uppercase tracking-wider mb-2.5 flex items-center gap-2">{icon} {title}</h4>
    {children}
  </div>
);

// ─── Upcoming Work hero card ──────────────────────────────────────────
// Matches the "Workiz" spec the user shared. Tap → opens the job detail
// panel (same as Recent Jobs rows, no new flow).
const UpcomingJobCard = ({ job, techName, onTap }) => {
  const addrLine1 = job.customer_address || job.customer_name || "Job site";
  const cityState = [job.address_city, job.address_state].filter(Boolean).join(", ");
  const time =
    formatScheduledTime(job.scheduled_time) ||
    extractTimeFromDate(job.scheduled_date) ||
    formatTime(job.scheduled_date);
  const price = job.total_charged || job.total_price || 0;
  // job_display_id is usually "JOB-0119" — strip the prefix so the literal
  // "JOB" tag in the UI doesn't render as "JOB JOB-0119".
  const rawLabel = job.job_display_id || (job.job_id ? job.job_id.slice(-4).toUpperCase() : "");
  const jobLabel = rawLabel.replace(/^JOB-?/i, "").replace(/^#/, "") || "—";
  const serviceLabel = (job.service_type || job.service_name || "Service").toString().replace(/_/g, " ");

  return (
    <button
      onClick={onTap}
      className="w-full text-left bg-card rounded-2xl border border-border/60 shadow-sm p-4 hover:shadow-md hover:-translate-y-0.5 active:scale-[0.99] transition-all"
      data-testid="upcoming-job-card"
    >
      {/* Tag row */}
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-1.5 flex-wrap text-[10px] font-semibold tracking-wide">
          <span className="text-primary uppercase">JOB #{jobLabel}</span>
          <span className="text-muted-foreground/40">|</span>
          <span className="text-muted-foreground uppercase truncate max-w-[100px]">{serviceLabel}</span>
        </div>
        <div className="text-muted-foreground/50 p-1 -mr-1">
          <MoreHorizontal className="w-4 h-4" />
        </div>
      </div>

      {/* Address */}
      <div className="mb-3">
        <p className="text-[15px] font-bold text-foreground leading-tight font-['Manrope'] truncate">
          {addrLine1}
        </p>
        {cityState && (
          <p className="text-xs text-muted-foreground/70 mt-0.5 truncate">{cityState}</p>
        )}
      </div>

      {/* Pills row */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {price > 0 && (
          <span
            className="px-3 py-1.5 rounded-lg text-xs font-semibold text-foreground bg-muted/50 border border-border/60"
            data-testid="upcoming-price-pill"
          >
            {moneyFmt(price)}
          </span>
        )}
        {time && (
          <span
            className="px-3 py-1.5 rounded-lg text-xs font-semibold text-pink-700 bg-pink-50 border border-pink-200/60 inline-flex items-center gap-1.5"
            data-testid="upcoming-time-pill"
          >
            <Clock className="w-3 h-3" />
            {time}
          </span>
        )}
      </div>

      {/* Tech footer */}
      <div className="flex items-center justify-between pt-2 border-t border-border/40">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <User className="w-3 h-3" />
          <span className="truncate">{techName || "Assigned"}</span>
        </div>
        <InitialsAvatar name={techName} />
      </div>
    </button>
  );
};

// ─── Main Dashboard ───────────────────────────────────────────────────
const TechDashboard = () => {
  const { timezone } = useTimezone();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState("today");
  const [panel, setPanel] = useState(null);
  const [panelJobs, setPanelJobs] = useState([]);
  const [panelLoading, setPanelLoading] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const navigate = useNavigate();

  // Read the tech's name once for the greeting. Cheap localStorage read,
  // safe in private/incognito (try/catch in the helper).
  const techUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("tech_user") || "{}");
    } catch { return {}; }
  }, []);
  const fullName = techUser.name || "there";
  const firstName = (fullName.split(/\s+/)[0] || "there").trim();

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/tech/dashboard`, {
        params: { date_filter: dateFilter, tz: timezone },
        ...getAuthConfig(),
      });
      setData(res.data);
    } catch (err) {
      console.error("Failed to load dashboard:", err);
    } finally {
      setLoading(false);
    }
  }, [dateFilter, timezone]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const openJobPanel = async (title, statusFilter, assignmentFilter) => {
    setPanel({ type: "jobs", title });
    setPanelLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);
      if (assignmentFilter) params.append("assignment_status", assignmentFilter);
      const res = await axios.get(`${API}/tech/jobs?${params.toString()}`, getAuthConfig());
      setPanelJobs(res.data.jobs || []);
    } catch {
      setPanelJobs([]);
    } finally {
      setPanelLoading(false);
    }
  };

  const openJobDetail = async (job) => {
    try {
      const res = await axios.get(`${API}/tech/jobs/${job.job_id}`, getAuthConfig());
      setSelectedDetail(res.data);
    } catch {
      setSelectedDetail(job);
    }
  };

  const stats = data?.stats || {};
  const jobs = data?.recent_jobs || [];
  const filterLabel = DATE_FILTERS.find((f) => f.value === dateFilter)?.label || dateFilter;

  // ── Derived: "Needs Attention" surfaces (a) jobs waiting for the tech
  //    to accept/reject (the trustworthy `stats.pending_approval` from the
  //    backend) and (b) any scheduled / in-progress job in the recent_jobs
  //    slice that's starting today or already overdue (best-effort signal —
  //    recent_jobs is capped at 10 so this is a lower-bound for today/overdue
  //    when there are very many). The tap action opens the pending-approval
  //    list panel since that's the most directly actionable.
  const needsAttention = useMemo(() => {
    const todayMidnight = new Date();
    todayMidnight.setHours(23, 59, 59, 999);
    const overdueOrToday = jobs.filter((j) => {
      if (!j.scheduled_date) return false;
      const ts = new Date(j.scheduled_date).getTime();
      if (Number.isNaN(ts)) return false;
      return (
        ts <= todayMidnight.getTime() &&
        ["scheduled", "in_progress"].includes(j.status)
      );
    });
    const pending = Number(stats.pending_approval) || 0;
    return {
      pending,
      todayOrOverdue: overdueOrToday.length,
      // Display total is the sum (with the recent_jobs cap caveat). The
      // subtitle below the count spells out the breakdown so users don't
      // misread the number as e.g. "27 unaccepted jobs".
      count: pending + overdueOrToday.length,
    };
  }, [jobs, stats.pending_approval]);

  const openNeedsAttention = () => {
    // We use the existing pending-approval panel path — most actionable.
    // The "overdue/today" subset is also visible from the recent-jobs
    // list rendered below.
    openJobPanel(
      `Needs Attention (${needsAttention.count})`,
      null,
      "pending",
    );
  };

  // Upcoming = first scheduled/in_progress job (already sorted newest-first
  // by the API; for true future ordering the user can dig into /tech/jobs).
  const upcomingJob = useMemo(
    () => jobs.find((j) => ["scheduled", "in_progress", "pending"].includes(j.status)),
    [jobs],
  );

  const updatedLabel = `Updated ${formatShortDate(new Date().toISOString())}`;

  return (
    <div className="space-y-5 pb-8" data-testid="tech-dashboard">
      {/* ─── Greeting header ─────────────────────────────────────────── */}
      <div
        className="relative rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white px-5 py-5 shadow-md overflow-hidden"
        data-testid="tech-greeting-header"
      >
        {/* Subtle gold accent in the corner so it feels on-brand */}
        <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-primary/10 blur-2xl pointer-events-none" />
        <div className="relative">
          <h1
            className="text-lg sm:text-xl font-bold font-['Manrope'] leading-tight"
            data-testid="tech-greeting-name"
          >
            Hey <span className="text-primary">{firstName}</span>, here is your upcoming day
          </h1>
          <p className="text-[11px] text-white/50 mt-1 flex items-center gap-1.5">
            <Clock className="w-3 h-3" />
            {updatedLabel}
          </p>
        </div>
      </div>

      {/* ─── Needs Attention banner ──────────────────────────────────── */}
      {needsAttention.count > 0 && (
        <button
          onClick={openNeedsAttention}
          className="w-full flex items-center justify-between rounded-2xl border border-purple-200/60 bg-purple-50/70 dark:bg-purple-900/20 dark:border-purple-800/40 px-4 py-3 shadow-sm hover:shadow-md active:scale-[0.99] transition-all"
          data-testid="needs-attention-banner"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-full bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center shrink-0">
              <Bell className="w-4 h-4 text-purple-600 dark:text-purple-300" />
            </div>
            <div className="min-w-0 text-left">
              <p className="text-sm font-semibold text-foreground">
                Needs Attention{" "}
                <span className="text-purple-600 dark:text-purple-300 font-bold">
                  ({needsAttention.count})
                </span>
              </p>
              <p className="text-[11px] text-muted-foreground/70 truncate">
                {needsAttention.pending > 0 && (
                  <>{needsAttention.pending} awaiting your response</>
                )}
                {needsAttention.pending > 0 && needsAttention.todayOrOverdue > 0 && " · "}
                {needsAttention.todayOrOverdue > 0 && (
                  <>{needsAttention.todayOrOverdue} starting today / overdue</>
                )}
              </p>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
        </button>
      )}

      {/* ─── Upcoming Work ───────────────────────────────────────────── */}
      <section data-testid="upcoming-work-section">
        <div className="flex items-center justify-between mb-2.5 px-0.5">
          <h2 className="text-sm font-bold text-foreground font-['Manrope']">Upcoming work</h2>
          {jobs.length > 1 && (
            <button
              onClick={() => navigate("/tech/jobs")}
              className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5"
              data-testid="view-all-upcoming-btn"
            >
              View all <ArrowRight className="w-3 h-3" />
            </button>
          )}
        </div>

        {loading ? (
          <Card className="border border-border/60 shadow-sm">
            <CardContent className="p-6 flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </CardContent>
          </Card>
        ) : upcomingJob ? (
          <UpcomingJobCard
            job={upcomingJob}
            techName={fullName !== "there" ? fullName : ""}
            onTap={() => openJobDetail(upcomingJob)}
          />
        ) : (
          <Card className="border border-border/60 shadow-sm">
            <CardContent className="p-6 text-center">
              <Wrench className="w-7 h-7 text-muted-foreground/20 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No upcoming jobs</p>
            </CardContent>
          </Card>
        )}
      </section>

      {/* ─── Dashboard (bullet stats) ────────────────────────────────── */}
      <section data-testid="dashboard-section">
        <div className="flex items-center justify-between mb-2.5 px-0.5">
          <h2 className="text-sm font-bold text-foreground font-['Manrope']">Dashboard</h2>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-muted-foreground">Display:</span>
            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger
                className="h-8 text-xs border-border/60 bg-card rounded-lg px-2.5 gap-1.5 min-w-[100px]"
                data-testid="date-filter-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DATE_FILTERS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 rounded-lg border-border/60"
              onClick={fetchDashboard}
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        <Card className="border border-border/60 shadow-sm bg-card">
          <CardContent className="p-0">
            <div className="px-4 py-3 border-b border-border/40">
              <p className="text-xs font-semibold text-foreground">{filterLabel}</p>
            </div>
            <ul className="divide-y divide-border/40">
              <BulletRow
                color="bg-emerald-500"
                label="Sales"
                value={moneyFmt(stats.revenue)}
                testid="bullet-sales"
                onClick={() => openJobPanel(`Revenue Breakdown (${filterLabel})`, "completed")}
              />
              <BulletRow
                color="bg-blue-500"
                label="Collected"
                value={moneyFmt(stats.collected)}
                testid="bullet-collected"
                onClick={() => openJobPanel(`Collected Payments (${filterLabel})`, "completed")}
              />
              <BulletRow
                color="bg-amber-500"
                label="Jobs Done"
                value={String(stats.completed_jobs ?? 0)}
                testid="bullet-jobs-done"
                onClick={() => openJobPanel(`Completed Jobs (${filterLabel})`, "completed")}
              />
              <BulletRow
                color="bg-purple-500"
                label="My Commission"
                value={moneyFmt(stats.earnings)}
                testid="bullet-commission"
                onClick={() => openJobPanel(`Commission Breakdown (${filterLabel})`, "completed")}
              />
            </ul>
          </CardContent>
        </Card>
      </section>

      {/* ─── Active / Pending strip ────────────────────────────────── */}
      <section className="grid grid-cols-2 gap-3" data-testid="active-pending-section">
        <StatCard
          icon={Briefcase}
          label="Active Jobs"
          value={stats.active_jobs ?? 0}
          sub="Current"
          color="text-blue-600"
          bgColor="bg-blue-100"
          clickable
          onClick={() => openJobPanel("Active Jobs", "in_progress")}
        />
        <StatCard
          icon={Clock}
          label="Pending Approval"
          value={stats.pending_approval ?? 0}
          sub="Waiting for your response"
          color="text-amber-600"
          bgColor="bg-amber-100"
          clickable
          onClick={() => openJobPanel("Pending Approval", null, "pending")}
        />
      </section>

      {/* ─── Recent Jobs ───────────────────────────────────────────── */}
      <Card className="border border-border/60 shadow-sm" data-testid="recent-jobs-card">
        <div className="flex items-center justify-between p-4 border-b border-border/50">
          <h2 className="text-sm font-semibold text-foreground font-['Manrope']">Recent Jobs</h2>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => navigate("/tech/jobs")}
            data-testid="view-all-jobs-btn"
          >
            View All <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
        {loading ? (
          <div className="p-8 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : jobs.length === 0 ? (
          <div className="p-8 text-center">
            <BarChart3 className="w-8 h-8 text-muted-foreground/20 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No recent jobs</p>
          </div>
        ) : (
          <div className="divide-y divide-border/30">
            {jobs.slice(0, 8).map((job, i) => (
              <div
                key={job.job_id || i}
                className="px-4 py-3 flex items-center justify-between hover:bg-muted/30 transition-colors cursor-pointer group"
                onClick={() => openJobDetail(job)}
                data-testid={`job-row-${i}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground truncate">{job.title || job.service_type || "Job"}</p>
                  <p className="text-[11px] text-muted-foreground/50 truncate">
                    {job.customer_name || "—"}{job.scheduled_date ? ` · ${formatDate(job.scheduled_date)}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-3 shrink-0">
                  {job.total_charged > 0 && <span className="text-xs font-semibold text-foreground">${job.total_charged}</span>}
                  <StatusBadge status={job.status} />
                  <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/20 group-hover:text-muted-foreground transition-colors" />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Panels */}
      {panel?.type === "jobs" && (
        <JobListPanel
          title={panel.title}
          jobs={panelJobs}
          loading={panelLoading}
          onClose={() => { setPanel(null); setPanelJobs([]); }}
          onJobClick={(job) => openJobDetail(job)}
        />
      )}
      {selectedDetail && (
        <JobDetailPanel job={selectedDetail} onClose={() => setSelectedDetail(null)} />
      )}
    </div>
  );
};

// Single row inside the Dashboard bullet card.
const BulletRow = ({ color, label, value, onClick, testid }) => (
  <li
    className="flex items-center justify-between gap-4 px-4 py-3 cursor-pointer hover:bg-muted/30 transition-colors group"
    onClick={onClick}
    data-testid={testid}
  >
    <div className="flex items-center gap-3 min-w-0">
      <span className={`w-2 h-2 rounded-full ${color} shrink-0`} aria-hidden="true" />
      <span className="text-sm text-foreground/80">{label}</span>
    </div>
    <div className="flex items-center gap-1.5 shrink-0">
      <span className="text-sm font-bold text-foreground tabular-nums">{value}</span>
      <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/20 group-hover:text-muted-foreground transition-colors" />
    </div>
  </li>
);

export default TechDashboard;
